
import axios from "axios";
import { getAuth, signOut } from "firebase/auth";

// ============================================
// OBFUSCATION CONFIGURATION
// ============================================
// Set to true to enable data obfuscation for demos/presentations
const ENABLE_DATA_OBFUSCATION = false;

// Fields that should NOT be obfuscated (e.g., IDs, status codes)
const OBFUSCATION_WHITELIST = ['id', 'status', 'code', 'type', 'role', 'timestamp', 'createdAt', 'updatedAt'];
let isSigningOutDisabledAccount = false;

// ============================================
// OBFUSCATION UTILITIES
// ============================================
const obfuscationUtils = {
  // Detect if a string is a numeric value
  isNumericString: (str) => {
    if (typeof str !== 'string') return false;
    // Check if string is a valid number (including decimals and negatives)
    return !isNaN(str) && !isNaN(parseFloat(str)) && str.trim() !== '';
  },
  
  // Randomize a numeric string while keeping it numeric
  randomizeNumericString: (numStr) => {
    try {
      const num = parseFloat(numStr);
      const isNegative = num < 0;
      const numAbsStr = Math.abs(num).toString();
      const hasDecimal = numStr.includes('.');
      
      let randomNum;
      if (hasDecimal) {
        const [intPart, decPart] = numAbsStr.split('.');
        const intDigits = intPart.length;
        const decDigits = decPart ? decPart.length : 2;
        
        // Generate random number with same decimal places
        randomNum = (Math.random() * Math.pow(10, intDigits)).toFixed(decDigits);
      } else {
        const digitCount = numAbsStr.length;
        randomNum = Math.floor(Math.random() * Math.pow(10, digitCount));
      }
      
      const result = isNegative ? -randomNum : randomNum;
      // Return as string to maintain format
      return String(result);
    } catch (e) {
      console.warn("Error randomizing numeric string:", numStr, e);
      return numStr;
    }
  },

  // Detect if a string is a date
  isDate: (str) => {
    if (typeof str !== 'string') return false;
    // Matches common date formats: YYYY-MM-DD, MM/DD/YYYY, DD/MM/YYYY, ISO 8601, etc.
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}/, // MM/DD/YYYY or DD/MM/YYYY
      /^\d{1,2}-\d{1,2}-\d{4}/, // M-D-YYYY
      /^\d{4}\/\d{2}\/\d{2}/, // YYYY/MM/DD
      /^(\d{4})-?(\d{2})-?(\d{2})T/, // ISO 8601 datetime
    ];
    return datePatterns.some(pattern => pattern.test(str));
  },

  // Detect if a number is likely a phone number (10+ digits)
  isPhoneNumberDigits: (num) => {
    if (typeof num !== 'number') return false;
    const numStr = Math.abs(num).toString();
    // Phone numbers are typically 10-15 digits
    return numStr.length >= 10 && numStr.length <= 15;
  },

  // Randomize a phone number (as number) while keeping format
  randomizePhoneNumberDigits: (num) => {
    try {
      const numStr = Math.abs(num).toString();
      const isNegative = num < 0;
      
      // Generate random digits of same length
      const randomDigits = Array.from({ length: numStr.length }, () => 
        Math.floor(Math.random() * 10)
      ).join('');
      
      const randomNum = parseInt(randomDigits, 10);
      return isNegative ? -randomNum : randomNum;
    } catch (e) {
      console.warn("Error randomizing phone number digits:", num, e);
      return num;
    }
  },

  // Detect if a string is a phone number
  isPhoneNumber: (str) => {
    if (typeof str !== 'string') return false;
    // Matches: +1-234-567-8900, (123) 456-7890, 123-456-7890, 1234567890, etc.
    const phonePattern = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
    return phonePattern.test(str.replace(/\s/g, ''));
  },

  // Randomize a date while keeping the format
  randomizeDate: (dateStr) => {
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      
      // Generate random date within same year
      const year = date.getFullYear();
      const randomDate = new Date(year, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1);
      
      // Preserve original format
      if (dateStr.includes('T')) {
        // ISO format with time
        return randomDate.toISOString();
      } else if (dateStr.includes('-')) {
        // YYYY-MM-DD format
        return randomDate.toISOString().split('T')[0];
      } else if (dateStr.includes('/')) {
        // MM/DD/YYYY or other slash format
        const month = String(randomDate.getMonth() + 1).padStart(2, '0');
        const day = String(randomDate.getDate()).padStart(2, '0');
        const year = randomDate.getFullYear();
        return `${month}/${day}/${year}`;
      }
    } catch (e) {
      // If date parsing fails, return original
    }
    return dateStr;
  },

  // Randomize phone number while keeping format
  randomizePhoneNumber: (phoneStr) => {
    // Extract only digits from the phone number
    const digitsOnly = phoneStr.replace(/\D/g, '');
    if (digitsOnly.length < 10) return phoneStr;
    
    // Generate random digits
    const randomDigits = Array.from({ length: digitsOnly.length }, () => 
      Math.floor(Math.random() * 10)
    ).join('');
    
    // Preserve original format by replacing digits
    let result = phoneStr;
    let randomIndex = 0;
    result = result.replace(/\d/g, () => randomDigits[randomIndex++] || '0');
    
    return result;
  },

  // Generate random string of same length as original
  randomizeString: (str) => {
    if (!str || typeof str !== 'string') return str;
    const length = str.length;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  },

  // Generate random number with same digit count as original
  randomizeNumber: (num) => {
    if (typeof num !== 'number') return num;
    const numStr = Math.abs(num).toString();
    const isNegative = num < 0;
    const digitCount = numStr.includes('.') ? numStr.split('.') : [numStr.length];
    const randomNum = Math.floor(Math.random() * Math.pow(10, digitCount[0]));
    return isNegative ? -randomNum : randomNum;
  },

  // Check if a field should be obfuscated
  shouldObfuscate: (key) => {
    return !OBFUSCATION_WHITELIST.some(whitelisted => 
      key.toLowerCase().includes(whitelisted.toLowerCase())
    );
  },

  // Recursively obfuscate nested objects and arrays
  obfuscateData: (data) => {
    if (data === null || data === undefined) return data;

    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => obfuscationUtils.obfuscateData(item));
    }

    // Handle objects
    if (typeof data === 'object') {
      const obfuscated = {};
      for (const [key, value] of Object.entries(data)) {
        if (!obfuscationUtils.shouldObfuscate(key)) {
          // Keep whitelisted fields as-is
          obfuscated[key] = value;
        } else if (typeof value === 'string') {
          // Check for special string types first
          if (obfuscationUtils.isNumericString(value)) {
            // Handle numeric strings
            obfuscated[key] = obfuscationUtils.randomizeNumericString(value);
          } else if (obfuscationUtils.isDate(value)) {
            obfuscated[key] = obfuscationUtils.randomizeDate(value);
          } else if (obfuscationUtils.isPhoneNumber(value)) {
            obfuscated[key] = obfuscationUtils.randomizePhoneNumber(value);
          } else {
            // Obfuscate regular strings
            obfuscated[key] = obfuscationUtils.randomizeString(value);
          }
        } else if (typeof value === 'number') {
          // Check if it's a phone number first
          if (obfuscationUtils.isPhoneNumberDigits(value)) {
            obfuscated[key] = obfuscationUtils.randomizePhoneNumberDigits(value);
          } else {
            // Obfuscate as regular number
            obfuscated[key] = obfuscationUtils.randomizeNumber(value);
          }
        } else if (typeof value === 'object') {
          // Recursively obfuscate nested objects/arrays
          obfuscated[key] = obfuscationUtils.obfuscateData(value);
        } else {
          // Keep other types as-is (boolean, null, etc.)
          obfuscated[key] = value;
        }
      }
      return obfuscated;
    }

    // Handle primitives
    if (typeof data === 'string') {
      if (obfuscationUtils.isNumericString(data)) {
        return obfuscationUtils.randomizeNumericString(data);
      } else if (obfuscationUtils.isDate(data)) {
        return obfuscationUtils.randomizeDate(data);
      } else if (obfuscationUtils.isPhoneNumber(data)) {
        return obfuscationUtils.randomizePhoneNumber(data);
      }
      return obfuscationUtils.randomizeString(data);
    }
    if (typeof data === 'number') {
      return obfuscationUtils.randomizeNumber(data);
    }

    return data;
  }
};

// Derive the API base URL. Prefer the env var; otherwise fall back to same-origin /api.
const fallbackOrigin = typeof window !== "undefined" ? window.location.origin : "";
const apiBaseUrl = (process.env.REACT_APP_BASE_URL || (fallbackOrigin && `${fallbackOrigin}/api`)).replace(/\/$/, "");

// Create axios instance
const api = axios.create({
  // Use environment variable for baseURL, fallback to same-origin /api when unset
  baseURL: apiBaseUrl || "http://localhost:5001",
  timeout: 30000, // 30 second timeout

  headers: {
    "Content-Type": "application/json",
    'Access-Control-Allow-Origin': '135.148.148.37',
    "Access-Control-Allow-Methods": "GET, POST, PUT",
    "Access-Control-Allow-Headers": "Content-Type",
    //mode: 'no-cors',
    //'Authorization': key,
    //withCredentials: true,
  },
});

const isAbortedRequestError = (error) => {
  if (!error) return false;

  return (
    axios.isCancel?.(error) ||
    error.code === "ERR_CANCELED" ||
    error.name === "CanceledError" ||
    error.name === "AbortError" ||
    error.message === "canceled" ||
    error.message === "Request aborted"
  );
};

// Add axios.all and axios.spread to the api object
api.all = axios.all;
api.spread = axios.spread;

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  async (config) => {
    // Let the browser set multipart boundaries automatically for FormData payloads.
    if (typeof FormData !== "undefined" && config?.data instanceof FormData) {
      if (config.headers?.common?.["Content-Type"]) {
        delete config.headers.common["Content-Type"];
      }
      if (config.headers?.post?.["Content-Type"]) {
        delete config.headers.post["Content-Type"];
      }
      if (config.headers?.["Content-Type"]) {
        delete config.headers["Content-Type"];
      }
    }

    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      } catch (e) {
        // If token retrieval fails (e.g., network), proceed without token
        console.warn("API: Failed to fetch Firebase ID token, continuing without Authorization.", e);
      }
    }
    
    // Log the request URL for debugging
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle auth errors and apply obfuscation
api.interceptors.response.use(
  (response) => {
    // Apply obfuscation if enabled
    if (ENABLE_DATA_OBFUSCATION && response.data) {
      try {
        response.data = obfuscationUtils.obfuscateData(response.data);
      } catch (obfuscationError) {
        console.error("Error during data obfuscation:", obfuscationError);
        // Return response without obfuscation if there's an error
      }
    }
    return response;
  },
  async (error) => {
    // Enhanced error logging with URL information
    let requestUrl = "Unknown URL";
    
    if (error.config) {
      // Ensure we don't duplicate the base URL
      requestUrl = error.config.url;
      // If the URL already includes the base URL, don't prepend it again
      if (error.config.baseURL && !requestUrl.startsWith('http')) {
        requestUrl = `${error.config.baseURL}${requestUrl}`;
      }
    }

    // Log network errors for debugging
    if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
      console.warn(`Network error for ${requestUrl}:`, error.message);
    }
        
    // Handle authentication errors
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      // Handle authentication errors (redirect to login, etc.)
      // window.location.href = '/login';
    }

    if (error.response?.status === 403 && error.response?.data?.accountDisabled) {
      const auth = getAuth();
      if (auth.currentUser && !isSigningOutDisabledAccount) {
        isSigningOutDisabledAccount = true;
        try {
          await signOut(auth);
        } catch (signOutError) {
          console.warn("Could not fully sign out disabled account session:", signOutError);
        } finally {
          isSigningOutDisabledAccount = false;
        }

        try {
          window.localStorage.removeItem("Email");
        } catch (_) {
          // ignore storage failures
        }

        if (typeof window !== "undefined" && window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Export obfuscation utilities for external control if needed
export { obfuscationUtils, ENABLE_DATA_OBFUSCATION, isAbortedRequestError };

/*

import { 
  createUserWithEmailAndPassword, 
  sendSignInLinkToEmail, 
  signInWithEmailAndPassword, 
  signInWithEmailLink, 
  signOut,
  getAuth
} from "firebase/auth";

const auth = getAuth();

export const doCreateUserWithEmailAndPassword = async (email, password) => {
  return createUserWithEmailAndPassword(auth, email, password);
}

export const doSignInWithEmailAndPassword = async (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
}

export const doSignInWithEmailLink = async (email, link) => {
  return signInWithEmailLink(auth, email, link);
}

const actionCodeSettings = {
  url: "http://localhost:3000/",
  handleCodeInApp: true,
  //linkDomain: "http://localhost:3000/",
};

export const doSendSignInLinkToEmail = (email) => {
  return sendSignInLinkToEmail(auth, email, actionCodeSettings);
}

export const doSignOut = () => {
  return signOut(auth);
}

 */
